import React from 'react';
import { Avatar } from '@shoflo/polymer/components/Avatar/Avatar';
import { UserValidator } from '@shoflo/polymer/validators/UserValidator/UserValidator';
import { components } from 'react-select';
import Creatable from 'react-select/creatable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

import _ from 'underscore';
import styled from 'styled-components';

const StyledSelect = styled(Creatable)`
  width: 100%;

  div[class*='multiValue'] {
    background: none !important;
  }
`;

const StyledLabel = styled.div`
  display: flex;
  align-items: center;
  background: #f0f2f6;
  font-size: 13px;
  padding: 8px;
  border-radius: 20px 0 0 20px;
`;

const StyledDeleteLabel = styled.div`
  display: flex;
  align-items: center;
  background: #f0f2f6;
  padding: 0 10px 2px 0;
  border-radius: 0 20px 20px 0;
  font-size: 20px;
  cursor: pointer;
`;

const StyledOption = styled(components.Option)`
  display: ${props => (props.hide ? 'none' : 'flex')} !important;
  align-items: center;
`;

const OptionUserName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #333;
`;

const OptionEmail = styled.div`
  font-size: 14px;
  color: #767676;
`;

const AvatarWrapper = styled.div`
  margin-right: 8px;
  min-width: 32px;
`;

const UserIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #333;
  color: #fff;

  width: ${props => (props.size ? props.size : '24px')};
  height: ${props => (props.size ? props.size : '24px')};
  margin-right: 8px;
  font-size: 12px;
  text-align: center;

  border-radius: 100%;
`;

export const MultiValueLabel = ({ data }) => {
  const { name, user } = data;
  return (
    <StyledLabel>
      {user ? (
        <AvatarWrapper>
          <Avatar user={user} width="24px" height="24px" />
        </AvatarWrapper>
      ) : (
        <UserIcon>
          <FontAwesomeIcon icon={faUser} />
        </UserIcon>
      )}
      {name}
    </StyledLabel>
  );
};

MultiValueLabel.propTypes = {
  data: PropTypes.object
};

export const MultiValueRemove = ({ innerProps }) => {
  const { onClick } = innerProps;
  return <StyledDeleteLabel onClick={onClick}>×</StyledDeleteLabel>;
};

MultiValueRemove.propTypes = {
  innerProps: PropTypes.object
};

export const Option = props => {
  const { data, options, value } = props;

  const hasOption = options.filter(o =>
    o.email ? o.email.includes(value) : false
  );

  const { email, name, user } = data;

  return (
    <StyledOption
      data-testid="crewSelectOption"
      {...props}
      hide={hasOption.length ? true : false}
    >
      {user ? (
        <AvatarWrapper>
          <Avatar user={user} width="32px" height="32px" />
        </AvatarWrapper>
      ) : (
        <AvatarWrapper>
          <UserIcon size="32px">
            <FontAwesomeIcon icon={faUser} />
          </UserIcon>
        </AvatarWrapper>
      )}
      <div>
        <OptionUserName>{name ? name : value}</OptionUserName>
        <OptionEmail>{email}</OptionEmail>
      </div>
    </StyledOption>
  );
};

Option.propTypes = {
  data: PropTypes.object,
  options: PropTypes.array,
  value: PropTypes.string
};

export const CrewSelect = ({
  options,
  selectedOptions,
  onChange,
  placeholder,
  defaultMenuIsOpen,
  styles,
  disableLabels
}) => {
  const data = options.map(item => {
    return {
      value: item._id,
      label: `${item.firstName} ${item.lastName} | ${item.email}`,
      name: `${item.firstName} ${item.lastName}`,
      email: item.email,
      photo: item.photo || null,
      user: item,
      role: 'readOnly',
      availableTools: []
    };
  });

  const components = {
    MultiValueLabel,
    MultiValueRemove,
    Option
  };

  // eslint-disable-next-line react/display-name
  if (disableLabels) components.MultiValueContainer = () => null;

  return (
    <StyledSelect
      data-testid="crewSelect"
      styles={styles}
      defaultMenuIsOpen={defaultMenuIsOpen}
      isMulti
      components={components}
      placeholder={placeholder}
      value={selectedOptions}
      options={data}
      onChange={selectedOptions => {
        selectedOptions =
          selectedOptions &&
          selectedOptions.map(o => {
            if (o.__isNew__) {
              const matches = o.label.match(
                /([a-zA-Z0-9._+-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi
              );
              return (matches || []).map(email => {
                return {
                  label: email.trim(),
                  value: email.trim(),
                  email: email.trim()
                };
              });
            } else {
              return o;
            }
          });

        selectedOptions = _.flatten(selectedOptions);
        selectedOptions = _.uniq(selectedOptions, x => x.email);

        let next = selectedOptions;

        if (next) {
          next = next
            .map(o => {
              if (!o.user) {
                return {
                  value: o.label.trim(),
                  label: o.label.trim(),
                  name: o.label.trim(),
                  email: o.label.trim(),
                  photo: null,
                  user: null
                };
              }

              return o;
            })
            .filter(o => UserValidator.email(o.email.trim()));
        }

        onChange(next);
      }}
    />
  );
};

CrewSelect.defaultProps = {
  options: [],
  disableLabels: false
};

CrewSelect.propTypes = {
  options: PropTypes.array,
  selectedOptions: PropTypes.array,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  defaultMenuIsOpen: PropTypes.bool,
  styles: PropTypes.object,
  disableLabels: PropTypes.bool
};

export default CrewSelect;
